import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import api, { RetailerDto, RetailerVendorDto } from "../../../api";
import { createDownloadLink, DefaultMessageDialogOptions, MessageDialog, MessageDialogButton, MessageDialogButtons, MessageDialogIcon, MessageDialogOptions, notif } from "../../utils";
import { RetailerVendorDialog, RetailerVendorDialogAction } from "./RetailerVendorDialog";
import { RetailerVendorLogsDialog, RetailerVendorLogsDialogAction } from "./RetailerVendorLogsDialog";
import { RetailerVendorsGrid } from "./RetailerVendorsGrid";

export interface RetailerVendorsTabPageProps {
  retailer?: RetailerDto | null;
}

type MessageOrigin = "delete" | "send" | "download";

export const RetailerVendorsTabPage = (props: RetailerVendorsTabPageProps) => {
  const { retailer } = props;

  const [selected, setSelected] = useState<RetailerVendorDto | null>(null);
  const [action, setAction] = useState<RetailerVendorDialogAction>({ open: false });
  const [logsAction, setLogsAction] = useState<RetailerVendorLogsDialogAction>({ open: false });
  const [message, setMessage] = useState<MessageDialogOptions<MessageOrigin>>(DefaultMessageDialogOptions<MessageOrigin>());
  const [sendIsLoading, setSendIsLoading] = useState(false);
  const [downloadIsLoading, setDownloadIsLoading] = useState(false);

  const onRowSelected = (row: RetailerVendorDto | null) => {
    setSelected(row);
  };
  const onAdd = () => {
    if (retailer) {
      setAction({ open: true, retailer, vendorId: "" });
    }
  };
  const onEdit = () => {
    if (selected?.retailer) {
      setAction({ open: true, retailer: selected.retailer, vendorId: selected.vendorId });
    }
  };
  const onDelete = () => {
    if (selected?.retailerId && selected?.vendorId) {
      setMessage({
        open: true,
        title: "DELETE RETAILER VENDOR",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to delete retailer vendor '${selected.vendor?.name}'?`,
        checks: [{ label: "Delete all dependent entities", value: false }],
        origin: "delete",
      });
    }
  };
  const deleteRetailerVendor = async (cascade?: boolean) => {
    if (selected?.retailerId && selected?.vendorId) {
      try {
        await api.retailerVendor.delete(cascade ?? false, selected.vendorId, selected.retailerId);
      } catch (err) {
        notif(err, "error");
      }
    }
  };

  const onDialogSubmit = (dto: RetailerVendorDto | null) => {
    setAction({ open: false });
    setSelected(null);
  };

  const onSendClick = async () => {
    if (selected?.retailerId && selected?.vendorId) {
      setMessage({
        open: true,
        title: "SEND INVENTORY",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to send retailer '${selected.retailer?.name}' vendor '${selected.vendor?.name}' inventory?`,
        origin: "send",
      });
    }
  };

  const send = async () => {
    if (selected?.retailerId && selected?.vendorId) {
      setSendIsLoading(true);
      try {
        const result = await api.retailerVendor.send({ retailerId: selected.retailerId, vendorId: selected.vendorId } as any);
        if (result.isError) {
          notif(result.log, "error");
        } else {
          notif("Send successful");
        }
      } catch (err) {
        notif(err, "error");
      }
      setSendIsLoading(false);
    }
  };
  const onDownloadClick = async () => {
    if (selected?.retailerId && selected?.vendorId) {
      setMessage({
        open: true,
        title: "DOWNLOAD INVENTORY",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to download retailer '${selected.retailer?.name}' vendor '${selected.vendor?.name}' inventory?`,
        origin: "download",
      });
    }
  };

  const download = async () => {
    if (selected?.retailerId && selected?.vendorId) {
      setDownloadIsLoading(true);
      try {
        const file = await api.retailerVendor.export({ retailerId: selected.retailerId, vendorId: selected.vendorId } as any);
        createDownloadLink(file, window);
      } catch (err) {
        notif(err, "error");
      }
      setDownloadIsLoading(false);
    }
  };

  const onMessageSubmit = async (button: MessageDialogButton, origin?: MessageOrigin, checks?: boolean[]) => {
    setMessage({ open: false });
    if (origin === "delete" && button === MessageDialogButton.Yes) {
      await deleteRetailerVendor(checks ? checks[0] : false);
    } else if (origin === "send" && button === MessageDialogButton.Yes) {
      await send();
    } else if (origin === "download" && button === MessageDialogButton.Yes) {
      await download();
    }
  };

  const onLogsView = () => {
    if (selected?.retailerId && selected?.vendorId) {
      setLogsAction({ open: true, retailer: selected.retailer, vendor: selected.vendor });
    }
  }
  const onLogsClose = () => {
    setLogsAction({ open: false });
  }

  useEffect(() => {}, [selected]);

  return (
    <Box>
      <RetailerVendorsGrid retailerId={retailer?.id || ""} onRowSelected={onRowSelected} onRowDoubleClick={onEdit} />
      <Box sx={{ p: 1 }} />
      <Stack spacing={2} direction="row">
        <Button variant="contained" disabled={!retailer?.id} onClick={onAdd}>
          Add
        </Button>
        <Button variant="contained" disabled={!selected?.retailerId} onClick={onEdit}>
          Edit
        </Button>
        <Button variant="contained" disabled={!selected?.retailerId} onClick={onDelete}>
          Delete
        </Button>
        <LoadingButton variant="contained" disabled={!selected?.retailerId} endIcon={<DriveFolderUploadIcon />} loading={sendIsLoading} loadingPosition="end" onClick={onSendClick}>
          Send
        </LoadingButton>
        <LoadingButton variant="contained" disabled={!selected?.retailerId} endIcon={<SaveAltOutlinedIcon />} loadingPosition="end" loading={downloadIsLoading} onClick={download}>
          Download
        </LoadingButton>
        <Button variant="contained" disabled={!selected?.retailerId} onClick={onLogsView}>
          Logs
        </Button>
      </Stack>
      <RetailerVendorDialog action={action} onSubmit={onDialogSubmit} />
      <RetailerVendorLogsDialog action={logsAction} onClose={onLogsClose} />
      <MessageDialog options={message} onSubmit={onMessageSubmit} />
    </Box>
  );
};
